import { isChartableSignal } from '../../lib/chartBuckets';
import { SignalActivityChart, type ChartSettings } from '../charts/SignalActivityChart';
import type { ChartVariant, Message, Template } from '../../types';

interface StatTile {
  value: string;
  label: string;
}

const DAY_MS = 24 * 60 * 60 * 1000;

// Four headline numbers computed from the channel's own chartable signal
// history (report-type messages excluded, same definition the chart
// itself uses) - deliberately NOT the hand-written "What happened / Why
// does it matter" marketing copy the landing/ project's static per-channel
// pages use (see PublicChannelViewScreen.tsx's header comment for why
// those are unrelated - no field on a real channel generates that prose
// today). Every tile here is a plain aggregate, so it stays true for any
// kind of channel (trains, builds, uptime, ...) rather than assuming one
// domain's vocabulary.
function buildStatTiles(messages: Message[]): StatTile[] {
  const signals = messages.filter(isChartableSignal);
  const now = Date.now();
  const thisWeek = signals.filter((m) => now - new Date(m.date).getTime() < 7 * DAY_MS);
  const lastWeek = signals.filter((m) => {
    const age = now - new Date(m.date).getTime();
    return age >= 7 * DAY_MS && age < 14 * DAY_MS;
  });

  const countByType = new Map<string, number>();
  for (const m of signals) {
    if (!m.type) continue;
    countByType.set(m.type, (countByType.get(m.type) ?? 0) + 1);
  }
  let mostActive = '—';
  let mostActiveCount = 0;
  for (const [type, count] of countByType) {
    if (count > mostActiveCount) {
      mostActive = type;
      mostActiveCount = count;
    }
  }

  let change = '—';
  if (lastWeek.length > 0) {
    const pct = Math.round(((thisWeek.length - lastWeek.length) / lastWeek.length) * 100);
    change = `${pct > 0 ? '+' : ''}${pct}%`;
  } else if (thisWeek.length > 0) {
    change = 'New';
  } else {
    change = '0%';
  }

  return [
    { value: String(signals.length), label: 'Total signals' },
    { value: mostActive, label: 'Most active' },
    { value: change, label: 'Week-over-week' },
    { value: String(thisWeek.length), label: 'Signals this week' },
  ];
}

interface PublicReportHeroProps {
  messages: Message[];
  templates: Template[];
  chartType: ChartVariant;
  onChartTypeChange: (v: ChartVariant) => void;
  accentHex: string;
  // See SignalActivityChart's identical props - threaded straight through
  // from PublicChannelViewScreen's SharedChannelView fetch.
  locked: boolean;
  initialSettings?: Partial<ChartSettings>;
  startFullscreen?: boolean;
  onFullscreenChange?: (open: boolean) => void;
  storageKey?: string;
}

// The "report" public-page design (ChannelManageScreen's "Public page
// design" setting / Channel.viewDesign) - a hero stat-tile row plus the
// same SignalActivityChart the classic Charts tab uses, shown permanently
// instead of behind a tab. Rendered by PublicChannelViewScreen above its
// existing Messages/Reports tabs once contentUnlocked; the identity header
// (avatar/title/description/badge/clone/request-to-join) stays identical
// between both designs, so this component owns only the stats+chart block.
export function PublicReportHero({
  messages,
  templates,
  chartType,
  onChartTypeChange,
  accentHex,
  locked,
  initialSettings,
  startFullscreen,
  onFullscreenChange,
  storageKey,
}: PublicReportHeroProps) {
  const tiles = buildStatTiles(messages);

  return (
    <div className="flex flex-col gap-4 shrink-0">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-px rounded-2xl overflow-hidden bg-black/5 dark:bg-white/10 border border-black/5 dark:border-white/10">
        {tiles.map((tile) => (
          <div key={tile.label} className="bg-white dark:bg-white/[0.03] px-3.5 py-3 flex flex-col gap-1">
            <span className="text-xl font-bold truncate" style={{ color: accentHex }}>
              {tile.value}
            </span>
            <span className="text-[11px] uppercase tracking-wide text-black/40 dark:text-white/40">{tile.label}</span>
          </div>
        ))}
      </div>
      <SignalActivityChart
        messages={messages}
        templates={templates}
        chartType={chartType}
        onChartTypeChange={onChartTypeChange}
        locked={locked}
        initialSettings={initialSettings}
        startFullscreen={startFullscreen}
        onFullscreenChange={onFullscreenChange}
        storageKey={storageKey}
      />
    </div>
  );
}
